import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { TextField, InputAdornment } from "@mui/material";
import { useEffect, useState } from "react";

interface DateTimePickerProps {
  value?: string; // ISO string
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  label?: string;
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick a date and time",
  className,
  disabled,
  error,
  fullWidth = true,
  label,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(value) : undefined
  );
  const [timeValue, setTimeValue] = useState<string>(() => {
    if (value) {
      const date = new Date(value);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }
    return "";
  });

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      setTimeValue(`${hours}:${minutes}`);
    } else {
      setSelectedDate(undefined);
      setTimeValue("");
    }
  }, [value]);

  const combineDateAndTime = (date: Date, time: string): string => {
    if (!time) {
      // No time provided, set to start of day
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      return newDate.toISOString();
    }

    const [hours, minutes] = time.split(":");
    // Create date from the selected date's year, month, day
    // and combine with the time in local timezone
    const newDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      parseInt(hours, 10),
      parseInt(minutes, 10),
      0,
      0
    );
    return newDate.toISOString();
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      setSelectedDate(undefined);
      setTimeValue("");
      onChange("");
      return;
    }

    setSelectedDate(date);
    // Combine the selected date with current time value
    const isoString = combineDateAndTime(date, timeValue);
    onChange(isoString);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value;
    setTimeValue(time);

    if (selectedDate) {
      const isoString = combineDateAndTime(selectedDate, time);
      onChange(isoString);
    }
  };

  // Display the actual time from the value, not from timeValue state
  const getDisplayValue = () => {
    if (!value) return "";
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return "";
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${format(date, "PPP")} ${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const displayValue = getDisplayValue();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div style={{ width: fullWidth ? "100%" : "auto" }}>
          <TextField
            fullWidth={fullWidth}
            label={label}
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            error={error}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarIcon
                    className="h-5 w-5"
                    style={{ color: "rgba(0, 0, 0, 0.54)" }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                cursor: disabled ? "not-allowed" : "pointer",
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: error ? undefined : "rgba(0, 0, 0, 0.87)",
                  },
                },
              },
            }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
          />
          <div className="flex items-center gap-2 p-3 border-t">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Input
              type="time"
              value={timeValue}
              onChange={handleTimeChange}
              className="w-full"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
